import { useState, useEffect } from "react";
import {
  Box,
  Card,
  Grid,
  IconButton,
  Typography,
  Divider,
  Chip,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import ProcesosService from "../../services/procesos.service";
import ReactPlayer from "react-player";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CodeIcon from "@mui/icons-material/Code";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { InputReadQuill } from "../../components";

const ProcesosGetByIdScreen = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [item, setItem] = useState(null);
  const [tabDiagrama, setTabDiagrama] = useState(0);
  const [pdfBlobUrls, setPdfBlobUrls] = useState({});

  const injectDiagramStyles = (html) => {
    if (!html) return "";
    const bgColor = isDarkMode ? "#1a2332" : "#ffffff";
    const dmValue = isDarkMode ? "1" : "0";
    let result = html.replace(
      /&quot;dark-mode&quot;:&quot;auto&quot;/g,
      `&quot;dark-mode&quot;:&quot;${dmValue}&quot;`,
    );
    const style = `<style>html,body{background-color:${bgColor} !important;margin:0;padding:0;}</style>`;
    if (result.includes("</head>"))
      return result.replace("</head>", `${style}</head>`);
    return style + result;
  };

  const actualizar = async () => {
    await ProcesosService.getById(id).then((resp) => {
      setItem(resp);
    });
  };
  useEffect(() => {
    actualizar();
    document.title = `Proceso`;
  }, []);

  // Fetch PDFs as blob to avoid X-Frame-Options blocking in iframe
  useEffect(() => {
    const created = {};
    ["html2_pro", "html3_pro", "html4_pro"].forEach((f) => {
      if (item?.[f]?.endsWith?.(".pdf")) {
        const url = `https://zeta.strongsystems.com.ar:8000/api/img?url=procesos/${item[f]}&token=${token}`;
        fetch(url)
          .then((res) => res.blob())
          .then((blob) => {
            const objUrl = URL.createObjectURL(blob);
            created[f] = objUrl;
            setPdfBlobUrls((prev) => ({ ...prev, [f]: objUrl }));
          })
          .catch((err) => console.error("Error loading PDF:", err));
      }
    });
    return () => {
      Object.values(created).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [item]);

  const diagramTabs = [
    item?.html1_pro && { label: "Diagrama", field: "html1_pro" },
    item?.html2_pro && { label: "Consideraciones", field: "html2_pro" },
    item?.html3_pro && { label: "Consideraciones 2", field: "html3_pro" },
    item?.html4_pro && { label: "Consideraciones 3", field: "html4_pro" },
  ].filter(Boolean);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        background: isDarkMode ? "#1a2332" : "#f5f5f5",
      }}
      className="side"
    >
      <Grid container p={{ xs: 2, md: 4 }} mb={10} spacing={3}>
        {/* Header mejorado con botón de regreso */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <IconButton
              sx={{
                boxShadow: "0 2px 8px rgba(15, 41, 75, 0.1)",
                background: isDarkMode ? "#2d3a52" : "#fff",
                width: 48,
                height: 48,
                border: isDarkMode ? "1px solid #3d4a62" : "1px solid #e5e7eb",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: isDarkMode ? "#90caf9" : "#0364ff",
                  borderColor: isDarkMode ? "#90caf9" : "#0364ff",
                  transform: "translateX(-4px)",
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                  },
                },
              }}
              onClick={() => navigate(-1)}
            >
              <ArrowBackIcon
                sx={{ color: isDarkMode ? "#90caf9" : "#15294b", fontSize: 20 }}
              />
            </IconButton>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #0364ff 0%, #0247b5 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(3, 100, 255, 0.3)",
                  }}
                >
                  <AccountTreeIcon sx={{ color: "#fff", fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: isDarkMode ? "#e0e0e0" : "#15294b",
                      fontWeight: 700,
                      fontSize: { xs: "1.5rem", md: "2rem" },
                      letterSpacing: "-0.02em",
                      lineHeight: 1.2,
                    }}
                  >
                    {item?.nombre_pro}
                  </Typography>
                  <Typography
                    sx={{
                      color: isDarkMode ? "#b0bec5" : "#64748b",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    Documentación del proceso
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Divider
            sx={{
              borderColor: isDarkMode ? "#3d4a62" : "#e5e7eb",
              borderWidth: 1,
            }}
          />
        </Grid>

        {/* Descripción principal */}
        {item?.descripcion_pro && (
          <Grid item xs={12}>
            <Card
              sx={{
                background: isDarkMode ? "#2d3a52" : "#ffffff !important",
                borderRadius: 2,
                border: isDarkMode ? "1px solid #3d4a62" : "1px solid #e5e7eb",
                boxShadow: "0 1px 3px rgba(15, 41, 75, 0.08)",
                p: 3,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    background:
                      "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccountTreeIcon sx={{ color: "#0364ff", fontSize: 18 }} />
                </Box>
                <Typography
                  sx={{
                    color: isDarkMode ? "#e0e0e0" : "#15294b",
                    fontWeight: 600,
                    fontSize: "1.1rem",
                  }}
                >
                  Descripción
                </Typography>
              </Box>
              <Box
                sx={{
                  "& .ql-editor": {
                    fontSize: "0.938rem",
                    color: isDarkMode ? "#b0bec5" : "#374151",
                    lineHeight: 1.7,
                  },
                  "& .ql-editor *": {
                    color: `${isDarkMode ? "#b0bec5" : "#374151"} !important`,
                  },
                }}
              >
                <InputReadQuill template={item?.descripcion_pro} />
              </Box>
            </Card>
          </Grid>
        )}

        {/* Sección de HTML/Diagramas */}
        {diagramTabs.length > 0 && (
          <>
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <CodeIcon
                  sx={{
                    color: isDarkMode ? "#90caf9" : "#0364ff",
                    fontSize: 24,
                  }}
                />
                <Typography
                  sx={{
                    color: isDarkMode ? "#e0e0e0" : "#15294b",
                    fontWeight: 600,
                    fontSize: "1.25rem",
                  }}
                >
                  Diagramas y Visualizaciones
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Card
                sx={{
                  background: isDarkMode ? "#2d3a52" : "#ffffff !important",
                  borderRadius: 2,
                  border: isDarkMode
                    ? "1px solid #3d4a62"
                    : "1px solid #e5e7eb",
                  boxShadow: "0 1px 3px rgba(15, 41, 75, 0.08)",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(3, 100, 255, 0.1)",
                    borderColor: isDarkMode ? "#90caf9" : "#0364ff",
                  },
                }}
              >
                {diagramTabs.length > 1 ? (
                  <>
                    <Box
                      sx={{
                        background: isDarkMode
                          ? "#1F2A40"
                          : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                        borderBottom: isDarkMode
                          ? "1px solid #3d4a62"
                          : "1px solid #e5e7eb",
                        px: 2,
                      }}
                    >
                      <Tabs
                        value={tabDiagrama}
                        onChange={(_, v) => setTabDiagrama(v)}
                        textColor="primary"
                        indicatorColor="primary"
                        sx={{
                          "& .MuiTab-root": {
                            color: isDarkMode ? "#b0bec5" : "#64748b",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                          },
                          "& .Mui-selected": {
                            color: isDarkMode
                              ? "#90caf9 !important"
                              : "#0364ff !important",
                          },
                          "& .MuiTabs-indicator": {
                            background: isDarkMode ? "#90caf9" : "#0364ff",
                          },
                        }}
                      >
                        {diagramTabs.map((tab, i) => (
                          <Tab key={i} label={tab.label} />
                        ))}
                      </Tabs>
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        background: isDarkMode ? "#1a2332" : "#ffffff",
                      }}
                    >
                      {diagramTabs.map(
                        (tab, i) =>
                          tabDiagrama === i &&
                          (item[tab.field]?.endsWith?.(".pdf") ? (
                            <iframe
                              key={tab.field}
                              src={pdfBlobUrls[tab.field] || ""}
                              width="100%"
                              height={600}
                              title={`Visor PDF - ${tab.label}`}
                              style={{
                                display: "block",
                                border: "none",
                                borderRadius: "4px",
                              }}
                            />
                          ) : (
                            <iframe
                              key={tab.field}
                              srcDoc={injectDiagramStyles(item[tab.field])}
                              width="100%"
                              height={500}
                              style={{
                                display: "block",
                                border: "none",
                                borderRadius: "4px",
                              }}
                            />
                          )),
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        background: isDarkMode
                          ? "#1F2A40"
                          : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                        px: 2,
                        py: 1.5,
                        borderBottom: isDarkMode
                          ? "1px solid #3d4a62"
                          : "1px solid #e5e7eb",
                      }}
                    >
                      <Chip
                        label={diagramTabs[0].label}
                        size="small"
                        sx={{
                          background: "#0364ff",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        background: isDarkMode ? "#1a2332" : "#ffffff",
                      }}
                    >
                      {item[diagramTabs[0].field]?.endsWith?.(".pdf") ? (
                        <iframe
                          src={pdfBlobUrls[diagramTabs[0].field] || ""}
                          width="100%"
                          height={600}
                          title={`Visor PDF - ${diagramTabs[0].label}`}
                          style={{
                            display: "block",
                            border: "none",
                            borderRadius: "4px",
                          }}
                        />
                      ) : (
                        <iframe
                          srcDoc={injectDiagramStyles(
                            item[diagramTabs[0].field],
                          )}
                          width="100%"
                          height={500}
                          style={{
                            display: "block",
                            border: "none",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Card>
            </Grid>
          </>
        )}

        {/* Sección de Videos */}
        {(item?.video1_pro || item?.video2_pro || item?.video3_pro) && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PlayCircleOutlineIcon
                sx={{ color: isDarkMode ? "#90caf9" : "#0364ff", fontSize: 24 }}
              />
              <Typography
                sx={{
                  color: isDarkMode ? "#e0e0e0" : "#15294b",
                  fontWeight: 600,
                  fontSize: "1.25rem",
                }}
              >
                Videos Tutoriales
              </Typography>
            </Box>
          </Grid>
        )}

        {item?.video1_pro && (
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                background: isDarkMode ? "#2d3a52" : "#ffffff !important",
                borderRadius: 2,
                border: isDarkMode ? "1px solid #3d4a62" : "1px solid #e5e7eb",
                boxShadow: "0 1px 3px rgba(15, 41, 75, 0.08)",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(3, 100, 255, 0.1)",
                  borderColor: isDarkMode ? "#90caf9" : "#0364ff",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Box
                sx={{
                  background: isDarkMode
                    ? "#1F2A40"
                    : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  px: 2,
                  py: 1.5,
                  borderBottom: isDarkMode
                    ? "1px solid #3d4a62"
                    : "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Chip
                  label="Video 1"
                  size="small"
                  icon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    background: "#0364ff",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
              <Box sx={{ p: 2 }}>
                <ReactPlayer
                  key={`Video${item.video1_pro}`}
                  url={`https://zeta.strongsystems.com.ar:8000/api/img?url=procesos/${item?.video1_pro}&token=${token}`}
                  width="100%"
                  height="250px"
                  controls
                  style={{ borderRadius: "4px", overflow: "hidden" }}
                />
              </Box>
            </Card>
          </Grid>
        )}

        {item?.video2_pro && (
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                background: isDarkMode ? "#2d3a52" : "#ffffff !important",
                borderRadius: 2,
                border: isDarkMode ? "1px solid #3d4a62" : "1px solid #e5e7eb",
                boxShadow: "0 1px 3px rgba(15, 41, 75, 0.08)",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(3, 100, 255, 0.1)",
                  borderColor: isDarkMode ? "#90caf9" : "#0364ff",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Box
                sx={{
                  background: isDarkMode
                    ? "#1F2A40"
                    : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  px: 2,
                  py: 1.5,
                  borderBottom: isDarkMode
                    ? "1px solid #3d4a62"
                    : "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Chip
                  label="Video 2"
                  size="small"
                  icon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    background: "#0364ff",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
              <Box sx={{ p: 2 }}>
                <ReactPlayer
                  key={`Video${item.video2_pro}`}
                  url={`https://zeta.strongsystems.com.ar:8000/api/img?url=procesos/${item?.video2_pro}&token=${token}`}
                  width="100%"
                  height="250px"
                  controls
                  style={{ borderRadius: "4px", overflow: "hidden" }}
                />
              </Box>
            </Card>
          </Grid>
        )}

        {item?.video3_pro && (
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                background: isDarkMode ? "#2d3a52" : "#ffffff !important",
                borderRadius: 2,
                border: isDarkMode ? "1px solid #3d4a62" : "1px solid #e5e7eb",
                boxShadow: "0 1px 3px rgba(15, 41, 75, 0.08)",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(3, 100, 255, 0.1)",
                  borderColor: isDarkMode ? "#90caf9" : "#0364ff",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Box
                sx={{
                  background: isDarkMode
                    ? "#1F2A40"
                    : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  px: 2,
                  py: 1.5,
                  borderBottom: isDarkMode
                    ? "1px solid #3d4a62"
                    : "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Chip
                  label="Video 3"
                  size="small"
                  icon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    background: "#0364ff",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
              <Box sx={{ p: 2 }}>
                <ReactPlayer
                  key={`Video${item.video3_pro}`}
                  url={`https://zeta.strongsystems.com.ar:8000/api/img?url=procesos/${item?.video3_pro}&token=${token}`}
                  width="100%"
                  height="250px"
                  controls
                  style={{ borderRadius: "4px", overflow: "hidden" }}
                />
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ProcesosGetByIdScreen;
